import { MemberInterface } from '@careapp/angular-sdk';
import { ADMIN_ROLE } from '@careapp/core';
import { createSelector } from 'reselect';

import { CAAction } from '../actions';
import * as actions from '../actions/index';

export const LOCAL_STORAGE_AUTH_KEY = 'careapp.auth';

export interface State {
  loading: boolean;
  resetPassword: boolean;
  error: string;
  auth?: { id: string; ttl: number; created: Date; userId: string; dirty?: boolean };
  user?: MemberInterface;
  roles?: Array<string>;
  ready: boolean;
}

const initialState = {
  loading: false,
  resetPassword: false,
  error: '',
  ready: false
};

export function reducer(state: State = initialState, action: CAAction): State {
  switch (action.type) {
    case actions.REQUEST_AUTH_LOGIN:
    case actions.REQUEST_LOGGED_IN_USER:
    case actions.REQUEST_AUTH_FORGOT_PASSWORD: {
      return { ...state, loading: true, resetPassword: false };
    }

    case actions.AUTH_RESET_ERRORS: {
      return { ...state, error: '', resetPassword: false };
    }

    case actions.REQUEST_AUTH_LOGOUT: {
      return { ...state, auth: null, loading: true };
    }

    case actions.RESPONSE_SUCCESS_AUTH_FORGOT_PASSWORD: {
      return { ...state, auth: null, loading: false, resetPassword: true, error: '' };
    }
    case actions.RESPONSE_SUCCESS_UPDATED_PROFILE:
    case actions.RESPONSE_SUCCESS_SAVE_PROFILE: {
      return { ...state, user: action.payload, loading: false };
    }

    case actions.APPLICATION_AUTHORIZED: {
      if (action.payload.user) {
        return { ...state, user: action.payload.user, ready: true };
      }
      return {
        ...state,
        ready: true
      };
    }

    case actions.RESPONSE_SUCCESS_USER_ROLES:
      return { ...state, roles: action.payload };

    case actions.RESPONSE_SUCCESS_AUTH_REFRESH:
    case actions.RESPONSE_SUCCESS_AUTH_LOGIN: {
      const newState = {
        ...state,
        loading: false,
        error: '',
        auth: {
          ...action.payload,
          created: new Date(),
          dirty: false
        }
      };
      return newState;
    }

    case actions.HYDRATE_AUTH: {
      return {
        ...state,
        auth: {
          ...(state.auth || {}),
          ...action.payload,
          dirty: true,
          date: new Date()
        }
      };
    }

    case actions.RESPONSE_FAIL_AUTH_LOGOUT:
    case actions.RESPONSE_SUCCESS_AUTH_LOGOUT: {
      return initialState;
    }

    case actions.RESPONSE_FAIL_AUTH_LOGIN:
    case actions.RESPONSE_FAIL_AUTH_FORGOT_PASSWORD: {
      const error = action.payload.code || action.payload;
      return { ...state, auth: null, loading: false, error };
    }

    case actions.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

export const createSelectors = (rootReducer: any) => ({
  getLoggedIn: createSelector(
    rootReducer,
    (state: State): boolean => state.auth && typeof state.auth.id === 'string'
  ),
  getAuth: createSelector(rootReducer, (state: State) => state.auth),
  getAuthToken: createSelector(rootReducer, (state: State) => (state.auth ? state.auth.id : null)),
  getError: createSelector(rootReducer, (state: State) => state.error),
  getLoading: createSelector(rootReducer, (state: State) => state.loading),
  getPersisted: createSelector(rootReducer, (state: State) => {
    const persisted = { ...state };
    delete persisted.ready;
    delete persisted.loading;
    return persisted;
  }),
  getResetPassword: createSelector(rootReducer, (state: State) => state.resetPassword),
  getLoggedInUser: createSelector(rootReducer, (state: State) => state.user),
  getRoles: createSelector(rootReducer, (state: State) => state.roles),
  isAdmin: createSelector(
    rootReducer,
    (state: State) => state.roles && !!state.roles.find(value => value === ADMIN_ROLE)
  ),
  isReady: createSelector(rootReducer, (state: State) => (state ? state.ready : false)),
  getAuthLinkQueryParams: createSelector(rootReducer, (state: State) => {
    if (state.auth && state.auth.userId && state.auth.id) {
      return `accessToken=${state.auth.id}&userId=${state.auth.userId}`;
    } else {
      return '';
    }
  })
});
